import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  actOnIntakeReviewAPI,
  fetchIntakeReviewCardAPI,
  fetchIntakeReviewListAPI,
  refreshIntakeReviewsAPI,
} from "../service/intakeReviews";

/**
 * The reviewing attorney's queue.
 *
 * Two things here are deliberate and easy to "tidy" into uselessness:
 *
 * 1. **The card tracks how long it was open and what was expanded.** That
 *    is the evidence for which facts carry the decision, and it is sent
 *    with the decision it explains.
 * 2. **A decision never optimistically updates the row.** The backend
 *    refuses a turn-down with no reasoning, and an optimistic move would
 *    show the intake leaving the pile a moment before it bounced back.
 */

export const fetchIntakeReviewAvailability = createAsyncThunk(
  "intakeReviews/fetchAvailability",
  async () => {
    try {
      const response = await fetchIntakeReviewListAPI("pending");
      return {
        available: true,
        count: response?.data?.counts?.pending ?? 0,
      };
    } catch (error) {
      // The nav entry hides itself when the routes are not deployed or
      // the user is off the reviewer allowlist — same posture as the
      // finance queue, so nobody sees a door they cannot open.
      return { available: false, count: 0 };
    }
  }
);

export const fetchIntakeReviewQueue = createAsyncThunk(
  "intakeReviews/fetchQueue",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { scope } = getState().intakeReviews;
      const response = await fetchIntakeReviewListAPI(scope);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load the queue");
    }
  }
);

export const refreshIntakeReviews = createAsyncThunk(
  "intakeReviews/refresh",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await refreshIntakeReviewsAPI();
      // Re-read the pile: the refresh reports what it pulled, but the
      // rows themselves come from the list.
      await dispatch(fetchIntakeReviewQueue());
      return response?.data?.summary || {};
    } catch (error) {
      return rejectWithValue(
        error?.message || "Could not check for new intakes"
      );
    }
  }
);

export const openIntakeReviewCard = createAsyncThunk(
  "intakeReviews/openCard",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchIntakeReviewCardAPI(id);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to open that intake");
    }
  }
);

export const actOnIntakeReview = createAsyncThunk(
  "intakeReviews/act",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await actOnIntakeReviewAPI(id, payload);
      return {
        id,
        action: payload.action,
        note: payload.note,
        ...(response?.data || {}),
      };
    } catch (error) {
      return rejectWithValue({
        message: error?.message || "Failed to record that decision",
        field: error?.field,
      });
    }
  }
);

const initialState = {
  // null = not yet probed, so the nav never flashes an icon then drops it.
  available: null,
  pendingCount: 0,

  view: "list", // "list" | "card"
  scope: "pending", // "pending" | "decided" | "mine"

  queue: { items: [], total: 0, counts: {} },
  queueLoading: false,
  queueError: null,

  // The last sync outcome, so an empty pile can say WHY it is empty.
  // Without it, "nothing needs deciding" and "the sync has been failing
  // for an hour" look identical on screen.
  lastSync: null,
  refreshing: false,
  refreshError: null,

  card: null,
  cardLoading: false,
  cardError: null,

  // Which collapsed sections the reviewer opened on the current card, and
  // when the card was opened. Reset per card, sent with the decision.
  sectionsOpened: [],
  openedAt: null,

  acting: false,
  actionError: null,
  actionErrorField: null,
  // Set when a decision contradicted the silent prediction: the surface
  // then asks for a sentence, which is the highest-value explanation the
  // firm can collect.
  explanationWanted: false,
  lastDecision: null,
  // What the reviewer wrote with the decision, so the reveal can show it
  // back beside the guess without re-fetching the card.
  lastNote: "",
  // The system's silent guess, revealed only AFTER the decision is
  // recorded (Matthew, 2026-09-19: "reveal its decision when the human
  // makes their selection"). Stays null while revealing is switched off,
  // for the permanent control group, and when the system had no opinion
  // — the server decides all of that; this side only displays it.
  reveal: null,

  // Confirmation that something happened. Without it the card simply
  // vanished and the reviewer had no way to tell the action had landed
  // — which is half of why a question that reached nobody was reported
  // as "it didn't do anything".
  flash: null,
};

// What to tell the reviewer once the card closes.
const FLASH_FOR = {
  signUp: "Signed up. The sign-up call task has been raised.",
  turnDown: "Turned down, and the reason is recorded.",
  referOut: "Referred out, and the receiving firm is recorded.",
  askQuestion: "Question sent to intake. It comes back once answered.",
  requestCall: "Call request sent. The card comes back to you with the answer.",
  callDone: "Sent back with what the client said.",
};

const IntakeReviewSlice = createSlice({
  name: "intakeReviews",
  initialState,
  reducers: {
    setIntakeReviewScope: (state, action) => {
      state.scope = action.payload;
    },
    setIntakeReviewView: (state, action) => {
      state.view = action.payload;
    },
    closeIntakeReviewCard: (state) => {
      state.view = "list";
      state.card = null;
      state.sectionsOpened = [];
      state.openedAt = null;
      state.actionError = null;
      state.actionErrorField = null;
      state.explanationWanted = false;
      state.lastDecision = null;
      state.lastNote = "";
      state.reveal = null;
    },
    // Recorded once per section per card: the signal is "did they need to
    // look at this", not "how many times did they toggle it".
    noteSectionOpened: (state, action) => {
      const key = action.payload;
      if (key && !state.sectionsOpened.includes(key)) {
        state.sectionsOpened.push(key);
      }
    },
    clearIntakeReviewActionError: (state) => {
      state.actionError = null;
      state.actionErrorField = null;
    },
    dismissExplanationRequest: (state) => {
      state.explanationWanted = false;
      // With no guess on screen there is nothing left to hold the card
      // open for; with one, the reviewer closes it with "Next case".
      if (!state.reveal) {
        state.view = "list";
        state.card = null;
        state.sectionsOpened = [];
        state.openedAt = null;
        state.lastDecision = null;
        state.lastNote = "";
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntakeReviewAvailability.fulfilled, (state, action) => {
        state.available = action.payload.available;
        state.pendingCount = action.payload.count;
      })

      .addCase(fetchIntakeReviewQueue.pending, (state) => {
        state.queueLoading = true;
        state.queueError = null;
      })
      .addCase(fetchIntakeReviewQueue.fulfilled, (state, action) => {
        state.queueLoading = false;
        state.available = true;
        state.queue = {
          items: action.payload?.items || [],
          total: action.payload?.total ?? 0,
          counts: action.payload?.counts || {},
        };
        state.pendingCount = action.payload?.counts?.pending ?? 0;
        state.lastSync = action.payload?.lastSync ?? null;
      })
      .addCase(fetchIntakeReviewQueue.rejected, (state, action) => {
        state.queueLoading = false;
        state.queueError = action.payload || "Failed to load the queue";
      })

      .addCase(refreshIntakeReviews.pending, (state) => {
        state.refreshing = true;
        state.refreshError = null;
      })
      .addCase(refreshIntakeReviews.fulfilled, (state) => {
        state.refreshing = false;
      })
      .addCase(refreshIntakeReviews.rejected, (state, action) => {
        state.refreshing = false;
        state.refreshError =
          action.payload || "Could not check for new intakes";
      })

      .addCase(openIntakeReviewCard.pending, (state) => {
        state.cardLoading = true;
        state.cardError = null;
      })
      .addCase(openIntakeReviewCard.fulfilled, (state, action) => {
        state.cardLoading = false;
        state.card = action.payload;
        state.view = "card";
        state.sectionsOpened = [];
        state.openedAt = Date.now();
        state.explanationWanted = false;
        state.lastDecision = null;
        state.lastNote = "";
        state.reveal = null;
      })
      .addCase(openIntakeReviewCard.rejected, (state, action) => {
        state.cardLoading = false;
        state.cardError = action.payload || "Failed to open that intake";
      })

      .addCase(actOnIntakeReview.pending, (state) => {
        state.acting = true;
        state.actionError = null;
        state.actionErrorField = null;
        state.flash = null;
      })
      .addCase(actOnIntakeReview.fulfilled, (state, action) => {
        state.acting = false;
        const {
          action: verb,
          status,
          explanationWanted,
          prediction,
          note,
        } = action.payload || {};
        // A guess worth showing: only a real decision carries one, and a
        // "no opinion" answer is not worth stopping the reviewer for.
        const showReveal =
          verb !== "assign" && Boolean(prediction && prediction.outcome);

        // A decided intake leaves the pile. Parking and asking a question
        // also take it out of the pending list, so the row is dropped in
        // every case except an in-place reassignment.
        // Adding a reason to a decision already made (explain) does not
        // remove anything: the intake left the pile when it was decided.
        if (verb !== "assign" && verb !== "explain") {
          state.queue.items = state.queue.items.filter(
            (row) => row.id !== action.payload.id
          );
          state.queue.total = Math.max(0, state.queue.total - 1);
          if (state.scope === "pending") {
            state.pendingCount = Math.max(0, state.pendingCount - 1);
          }
        }

        state.explanationWanted = Boolean(explanationWanted);
        state.lastDecision = status || null;
        state.lastNote = note || "";
        state.reveal = showReveal ? prediction : null;
        state.flash = FLASH_FOR[verb] || null;

        // Say so plainly when the decision was recorded here but has
        // not reached the case system, rather than implying work was
        // started that was not.
        if (action.payload?.writeBack === "disabled" && FLASH_FOR[verb]) {
          state.flash =
            "Recorded here. It has not been sent to the case system.";
        }

        // Hold the card open only while there is something to show or ask
        // — the system's guess, or a sentence about a surprising call.
        // Otherwise move the reviewer straight to the next intake, which
        // is the whole point of a one-at-a-time queue.
        if (verb === "assign") {
          if (state.card) {
            state.card.assignedTo = action.payload.assignedTo || null;
          }
        } else if (!explanationWanted && !showReveal) {
          state.view = "list";
          state.card = null;
          state.sectionsOpened = [];
          state.openedAt = null;
        } else if (state.card) {
          state.card.canDecide = false;
          state.card.status = status;
        }
      })
      .addCase(actOnIntakeReview.rejected, (state, action) => {
        state.acting = false;
        state.actionError =
          action.payload?.message || "Failed to record that decision";
        state.actionErrorField = action.payload?.field || null;
      });
  },
});

export const {
  setIntakeReviewScope,
  setIntakeReviewView,
  closeIntakeReviewCard,
  noteSectionOpened,
  clearIntakeReviewActionError,
  dismissExplanationRequest,
} = IntakeReviewSlice.actions;

// Defensive reads: an older persisted store may not carry this slice.
export const selectIntakeReviewAvailable = (state) =>
  state.intakeReviews?.available;
export const selectIntakeReviewPendingCount = (state) =>
  state.intakeReviews?.pendingCount ?? 0;

export default IntakeReviewSlice.reducer;
